/**
 * UserInviteModal component for inviting new users
 * Feature: 001-user-role-management (Phase 4 - T065)
 */
import { useState } from 'react';
import { X, Mail, Shield, AlertCircle } from 'lucide-react';
import type { UserRole, InviteUserRequest } from '../../types/user';

interface UserInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (data: InviteUserRequest) => Promise<void>;
}

export function UserInviteModal({ isOpen, onClose, onInvite }: UserInviteModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate email
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      await onInvite({ email, role });
      // Reset form on success
      setEmail('');
      setRole('user');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setEmail('');
      setRole('user');
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-card bg-brand-bg shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-brand-border px-6 py-4">
            <h3 className="text-lg font-semibold text-brand-text">
              Invite New User
            </h3>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-brand-muted hover:text-brand-text disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              {/* Error Alert */}
              {error && (
                <div className="rounded-input bg-brand-error/5 p-3 flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-brand-error flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-brand-error">{error}</div>
                </div>
              )}

              {/* Email Field */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-brand-text mb-1"
                >
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-brand-muted" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    className="block w-full pl-10 pr-3 py-2 border border-brand-border rounded-input focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:bg-brand-surface disabled:text-brand-muted"
                    placeholder="user@example.com"
                    required
                  />
                </div>
                <p className="mt-1 text-xs text-brand-muted">
                  An invitation email will be sent to this address
                </p>
              </div>

              {/* Role Field */}
              <div>
                <label
                  htmlFor="role"
                  className="block text-sm font-medium text-brand-text mb-1"
                >
                  Role
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Shield className="h-5 w-5 text-brand-muted" />
                  </div>
                  <select
                    id="role"
                    value={role}
                    onChange={(e) => setRole(e.target.value as UserRole)}
                    disabled={isSubmitting}
                    className="block w-full pl-10 pr-3 py-2 border border-brand-border rounded-input focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:bg-brand-surface disabled:text-brand-muted"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <p className="mt-1 text-xs text-brand-muted">
                  {role === 'admin'
                    ? 'Admin users have full access to all features'
                    : 'Regular users have limited access'}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-brand-border px-6 py-4 bg-brand-surface">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-brand-text bg-brand-bg border border-brand-border rounded-input hover:bg-brand-surface focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-brand-surface bg-brand-primary border border-transparent rounded-input hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-pill h-4 w-4 border-b-2 border-brand-surface"></div>
                    Inviting...
                  </>
                ) : (
                  'Send Invitation'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
